'use client';

import { useState } from 'react';

import { useOverlayItem } from '@flipster/overlay';
import { Modal } from '@flipster/react/design-system/modal/index';
import Formatted from '@flipster/react/design-utils/formatted';
import { FormattedNumberInput } from '@flipster/react/design-utils/formatted-input';
import ArrowRightIcon from '@flipster/react/icon/generated/ArrowRightIcon';
import { css } from '@flipster/styled-system/css';
import { center, flex, hstack, vstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';

import { slugToNameMapper } from './constants';
import { useExchangeTokens, useSpookyTreatsFestivalLuckyDraw } from './useLuckyDraw';

const validateAmount = (amount: number, availableTokens: number, id: string) => {
  if (amount > availableTokens) {
    throw new Error(`You don’t have enough ${slugToNameMapper[id].plural}.`);
  }
};

interface SelectEntryButtonsProps {
  token: number;
  changeableTokens: number;
  handleButtonSelect: (value: number) => void;
}

const SelectEntryButtons = ({ token, changeableTokens, handleButtonSelect }: SelectEntryButtonsProps) => {
  const selectEntryButtonItems = [
    { label: '+1', value: 1 },
    { label: '+2', value: 2 },
    { label: '+5', value: 5 },
    { label: '+10', value: 10 },
    { label: 'All', value: changeableTokens },
  ];

  const addEntries = (item: { label: string; value: number }) => {
    if (item.label === 'All') {
      handleButtonSelect(changeableTokens);
      return;
    }
    const next = token + item.value;
    handleButtonSelect(next);
  };

  return (
    <div className={flex({ gap: 6, mt: 12, w: '100%' })}>
      {selectEntryButtonItems.map((item) => {
        return (
          <button
            type='button'
            key={item.label}
            onClick={() => addEntries(item)}
            className={center({
              flex: 1,
              p: 10,
              rounded: 4,
              bgColor: 'background.default.level-2',
            })}
          >
            <span
              className={css({
                textStyle: 'Text/S/Strong',
                color: 'content.default.level-2',
                textAlign: 'center',
              })}
            >
              {item.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

interface ConvertModalProps {
  fromSlug: string;
  toSlug: string;
  cost: number;
}

export const ConvertModal = ({ fromSlug, toSlug, cost }: ConvertModalProps) => {
  const [fromAmount, setFromAmount] = useState(0);
  const [toAmount, setToAmount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const { onClose } = useOverlayItem();
  const { mutateAsync: exchangeTokens } = useExchangeTokens();
  const { luckyDrawData } = useSpookyTreatsFestivalLuckyDraw();

  const fromData = luckyDrawData.find((data) => data?.id === fromSlug);
  const fromAvailableTokens = fromData?.token ?? 0;
  const changeableTokens = Math.floor(fromAvailableTokens / cost);

  const disabled = fromAmount === 0 || toAmount === 0 || error !== null;

  const handleSetToAmount = (value: number) => {
    try {
      setError(null);
      setToAmount(value);
      const newFromAmount = value ? value * cost : 0;
      setFromAmount(newFromAmount);
      validateAmount(newFromAmount, fromAvailableTokens, fromSlug);
    } catch (error) {
      setError(error instanceof Error ? error.message : null);
    }
  };

  const handleExchangeTokens = () => {
    exchangeTokens({ from: { id: fromSlug, tokens: fromAmount }, to: { id: toSlug, tokens: toAmount } });
    onClose();
  };

  return (
    <Modal.Root onClose={onClose}>
      <Modal.Header>
        <Modal.Title label='Exchange sweets' />
      </Modal.Header>

      <Modal.Content>
        <div
          className={vstack({
            gap: 12,
            borderColor: 'border.default.level-1',
            borderBottom: '1px solid',
            alignItems: 'flex-start',
            py: 16,
          })}
        >
          <p className={labelStyle}>Available {slugToNameMapper[fromSlug].plural}</p>
          <Formatted.Number
            value={fromAvailableTokens}
            css={{ textStyle: 'Title/M/Monospace', color: 'content.default.level-4' }}
          />
        </div>
        <div className={labelWrapperStyle}>
          <span className={labelStyle}>From</span>
          <span className={labelStyle}>To</span>
        </div>
        <div className={inputWrapperStyle}>
          <div className={inputStyle}>{slugToNameMapper[fromSlug].singular}</div>
          <ArrowRightIcon size={20} color={token('colors.content.default.level-2')} />
          <div className={inputStyle}>{slugToNameMapper[toSlug].singular}</div>
        </div>
        <div className={labelWrapperStyle}>
          <span className={labelStyle}>Amount</span>
        </div>
        <div className={inputWrapperStyle}>
          <FormattedNumberInput value={fromAmount === 0 ? '' : fromAmount.toString()} className={inputStyle} disabled />
          <ArrowRightIcon size={20} color={token('colors.content.default.level-2')} />
          <FormattedNumberInput
            value={toAmount === 0 ? '' : toAmount.toString()}
            onChange={(value) => handleSetToAmount(value ? Number(value) : 0)}
            placeholder='Set amount'
            inputMode='numeric'
            className={inputStyle}
          />
        </div>
        <SelectEntryButtons
          changeableTokens={changeableTokens}
          token={toAmount}
          handleButtonSelect={handleSetToAmount}
        />
        {error && (
          <p
            className={css({
              textStyle: 'Text/M/Regular',
              color: 'content.state.negative',
              textAlign: 'center',
              pt: 32,
            })}
          >
            {error}
          </p>
        )}
      </Modal.Content>
      <Modal.Footer>
        <Modal.Button hierarchy='secondary' color='neutral' size='large' label='Cancel' onClick={onClose} />
        <Modal.Button size='large' label='Exchange' onClick={handleExchangeTokens} disabled={disabled} />
      </Modal.Footer>
    </Modal.Root>
  );
};

const labelStyle = css({
  textStyle: 'Text/L/Regular',
  color: 'content.default.level-3',
});

const labelWrapperStyle = hstack({
  justify: 'space-between',
  pt: 32,
});

const inputStyle = center({
  flex: 1,
  direction: 'row',
  gap: 8,
  p: 16,
  borderColor: 'border.default.level-3',
  border: '1px solid',
  rounded: 4,
  textAlign: 'center',
  textStyle: 'Text/L/Monospace/Strong',
  color: 'content.default.level-4',
  _placeholder: {
    textStyle: 'Text/L/Regular',
    color: 'content.default.level-1',
  },
  _disabled: {
    borderColor: 'border.interaction.disabled',
    color: 'content.default.level-2',
  },
});

const inputWrapperStyle = hstack({
  pt: 12,
  gap: 12,
});
